import dayjs from 'dayjs';
import { formatCompactNumber, pluralize } from '@/lib/utils';
import type { SavedAudience } from './audience-definition';

/**
 * The audience picker's data (NPJB-586): `GET /icp/audiences`, grouped by ICP, with the
 * Overview's live counts where they are cached.
 *
 * **An audience without an ICP is not shown**, anywhere on the page. Every other field
 * beyond the stored `SavedAudience` is optional: a row without live counts falls back to the
 * counts saved with it, and a row with no moved count shows no "new" marker.
 */

export interface PickerIcp {
  id: string;
  name: string;
  creatorName: string;
  createdAt: string;
}

export interface PickerAudience extends SavedAudience {
  icpId?: string | null;
  accounts?: number | null;
  committeeContacts?: number | null;
  countsSource?: 'live' | 'saved';
  /** `null` when not computed this week. */
  movedThisWeek?: number | null;
}

export interface PickerList {
  icps?: PickerIcp[];
  items: PickerAudience[];
}

export interface PickerGroup {
  icp: PickerIcp;
  audiences: PickerAudience[];
}

/**
 * The list with a saved row written over its own (NPJB-800 AC3), so the chips read the new
 * filters at once. The server's fields win; the picker's own (ICP, live counts) stay. A row
 * the list does not hold is left out: the refetch brings it with its ICP.
 */
export const mergeSavedIntoPicker = (list: PickerList, saved: SavedAudience): PickerList => ({
  ...list,
  items: list.items.map((audience) =>
    audience.id === saved.id ? { ...audience, ...saved } : audience,
  ),
});

/** The audiences the page may show: those under one of the list's ICPs. */
export const audiencesWithIcp = ({ icps = [], items }: PickerList): PickerAudience[] => {
  const known = new Set(icps.map((icp) => icp.id));
  return items.filter((audience) => !!audience.icpId && known.has(audience.icpId));
};

/**
 * One group per ICP, in the list's order, each with its active audiences. Archived
 * audiences are not offered, nor audiences without an ICP. An ICP with no active audience
 * still gets its block.
 */
export const groupAudiences = (list: PickerList): PickerGroup[] => {
  const active = audiencesWithIcp(list).filter((audience) => !audience.isArchived);
  return (list.icps ?? []).map((icp) => ({
    icp,
    audiences: active.filter((audience) => audience.icpId === icp.id),
  }));
};

/** The first ICP's first active audience — the audience a bare landing opens. */
export const firstPickerAudienceId = (list: PickerList): string | undefined =>
  groupAudiences(list).flatMap((group) => group.audiences)[0]?.id;

/**
 * "345 accounts · 3.6K contacts": live counts first, then the saved ones; `null` if neither.
 * Compact (K, M) so a large audience still fits the picker's line.
 */
export const audienceSize = (audience: PickerAudience): string | null => {
  const accounts = audience.accounts ?? audience.metrics.accounts;
  const contacts = audience.committeeContacts ?? audience.metrics.contacts;
  if (accounts === null || accounts === undefined || contacts === null || contacts === undefined) {
    return null;
  }
  return (
    `${formatCompactNumber(accounts)} ${pluralize(accounts, 'account', 'accounts')} · ` +
    `${formatCompactNumber(contacts)} ${pluralize(contacts, 'contact', 'contacts')}`
  );
};

/** "↑1 new" when that audience has movers this week; nothing when none or not computed. */
export const newMarker = (audience: PickerAudience): string | undefined => {
  const moved = audience.movedThisWeek;
  return typeof moved === 'number' && moved > 0 ? `↑${formatCompactNumber(moved)} new` : undefined;
};

/** The audience on screen cannot be archived from its own picker. */
export const canArchive = (audience: PickerAudience, openAudienceId: string | undefined) =>
  audience.id !== openAudienceId;

/** "30 Sep 2026 · Danielle Cho" — the ⋮ menu's first line. */
export const metaLine = (createdAt: string, creatorName: string): string =>
  `${dayjs(createdAt).format('D MMM YYYY')} · ${creatorName}`;

/** The picker's face when the account has an ICP but no audience to open yet. */
export const NO_AUDIENCE_LABEL = 'No audience yet';

/**
 * What the picker's face reads: the open audience's name; with none to open, that there is
 * none; otherwise `undefined`, which draws the loading placeholder.
 */
export const pickerLabel = (
  open: PickerAudience | undefined,
  noAudience: boolean,
): string | undefined => open?.title ?? (noAudience ? NO_AUDIENCE_LABEL : undefined);
